import { useState } from 'react';

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useTranslation } from '@/hooks/useTranslation';
import { CaretDown, ChevronDownIcon, ChevronRightIcon } from '@/components/icons';
import { Checkbox } from '@/components/form/Checkbox';
import { NumberCircle } from '../NumberCircle';

export type Node<ValueFieldName extends string> = {
  id: string;
} & {
  [key in ValueFieldName]: string;
};

type TreeNode<
  ChildrenFieldName extends string,
  ValueFieldName extends string,
> = Node<ValueFieldName> & {
  [key in ChildrenFieldName]?:
    | Array<TreeNode<ChildrenFieldName, ValueFieldName>>
    | null
    | undefined;
};

type CheckboxTreeProps<
  ChildrenFieldName extends string,
  ValueFieldName extends string,
> = {
  treeData: TreeNode<ChildrenFieldName, ValueFieldName>[];
  title?: string;
  selectedValues: Node<ValueFieldName>[];
  onChange: (selected: Node<ValueFieldName>[]) => void;
  disabled?: boolean;
  valueFieldName: ValueFieldName;
  childrenFieldName: ChildrenFieldName;
};

export const CheckboxTree = <
  ChildrenFieldName extends string,
  ValueFieldName extends string,
>({
  treeData,
  title,
  selectedValues,
  onChange,
  disabled,
  valueFieldName,
  childrenFieldName,
}: CheckboxTreeProps<ChildrenFieldName, ValueFieldName>) => {
  const { t } = useTranslation();

  const isSelected = (target: Node<ValueFieldName>) => {
    return selectedValues.find((item) => item.id === target.id);
  };

  const [expanded, setExpanded] = useState<string[]>([]);

  const handleExpandToggle = (nodeId: string): void => {
    expanded.includes(nodeId)
      ? setExpanded(expanded.filter((node) => node !== nodeId))
      : setExpanded(expanded.concat(nodeId));
  };

  const isAllSelected = () => {
    const allNodesQuantity = treeData.reduce((agg, node) => {
      const nodesQuantity = collectValues(node).length;
      agg += nodesQuantity;
      return agg;
    }, 0);

    return selectedValues.length === allNodesQuantity;
  };

  const selectAll = (): void => {
    const nodes = treeData.reduce((agg: Node<ValueFieldName>[], node) => {
      const nodes = collectValues(node);
      agg.push(...nodes);
      return agg;
    }, []);

    onChange(nodes);
  };

  const unselectAll = (): void => {
    onChange([]);
  };

  const isExpanded = (nodeId: string): boolean => {
    return expanded.includes(nodeId);
  };

  const collectValues = (
    n: TreeNode<ChildrenFieldName, ValueFieldName>,
  ): Node<ValueFieldName>[] => {
    let vals: Node<ValueFieldName>[] = [
      {
        id: n.id,
        [valueFieldName]: n[valueFieldName],
      } as Node<ValueFieldName>,
    ];
    if (n[childrenFieldName]) {
      n[childrenFieldName].forEach((child) => {
        vals = vals.concat(collectValues(child));
      });
    }
    return vals;
  };

  const toggleNode = (node: TreeNode<ChildrenFieldName, ValueFieldName>) => {
    const nodeValues = collectValues(node);

    let newSelected: Node<ValueFieldName>[];
    if (!isSelected(node)) {
      newSelected = [...selectedValues, ...nodeValues];
    } else {
      newSelected = selectedValues.filter((v) => {
        return !nodeValues.find((n) => n.id === v.id);
      });
    }

    newSelected = Object.values(
      newSelected.reduce((acc: Record<string, Node<ValueFieldName>>, value) => {
        acc[value.id] = value;
        return acc;
      }, {}),
    );

    onChange(newSelected);
  };

  const hasChildren = (node: TreeNode<ChildrenFieldName, ValueFieldName>) => {
    return node[childrenFieldName] && node[childrenFieldName].length > 0;
  };

  const renderTree = (nodes: TreeNode<ChildrenFieldName, ValueFieldName>[]) => {
    return nodes.map((node) => {
      const checked = isSelected(node);
      return (
        <div key={String(node.id)} className="flex-col pl-5 my-2">
          <div className="flex items-center">
            <div className="w-6">
              {hasChildren(node) && (
                <button
                  className="unstyled "
                  onClick={() => handleExpandToggle(node.id)}
                >
                  {isExpanded(node.id) ? (
                    <ChevronDownIcon className="w-4 h-4" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
            <label
              className="inline-flex items-center space-x-2 cursor-pointer"
              onClick={() => !disabled && toggleNode(node)}
            >
              <Checkbox checked={!!checked} disabled={disabled} />
              <span>{node[valueFieldName]}</span>
            </label>
          </div>
          {isExpanded(node.id) &&
            hasChildren(node) &&
            renderTree(node[childrenFieldName] || [])}
        </div>
      );
    });
  };

  const summarizeList = (): string => {
    const first3values = selectedValues
      .slice(0, 3)
      .map((val) => val[valueFieldName])
      .join(', ');

    return (
      first3values +
      (selectedValues.length > 3 ? ` + ${selectedValues.length - 3}` : '')
    );
  };

  return (
    treeData && (
      <Popover className="relative">
        <PopoverButton className="w-12/12 min-h-8 min-w-1/2 border border-gray-100 rounded sami-body bg-white flex items-center gap-2 p-2">
          <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap">
            {summarizeList()}
          </span>
          {selectedValues.length > 0 && (
            <NumberCircle number={selectedValues.length} size={6} />
          )}
          <CaretDown className="w-4 h-4 flex-shrink-0" />
        </PopoverButton>
        <PopoverPanel
          transition
          anchor="bottom start"
          className="absolute z-70 divide-y divide-white/5 rounded bg-white transition duration-200 ease-in-out
          border border-gray-100 shadow p-2 pr-4
          data-closed:-translate-y-1 data-closed:opacity-0"
        >
          <div className="flex ">
            <button
              disabled={isAllSelected()}
              className="unstyled p-2 text-purple-500"
              onClick={selectAll}
            >
              {t('global:selectAll')}
            </button>
            <span className="sami-body-bold p-2">|</span>
            <button
              disabled={selectedValues.length == 0}
              className="unstyled p-2 text-purple-500"
              onClick={unselectAll}
            >
              {t('global:selectNone')}
            </button>
          </div>
          {title && <h6>{title}</h6>}
          <div>{renderTree(treeData)}</div>
        </PopoverPanel>
      </Popover>
    )
  );
};
